import React, { useEffect, useRef, useState } from 'react';
import { PAGE_GUIDE_MEDIA } from './page-guide-media';

const PINK = '#7b1f40';
// Vite derives this public URL from the environment's existing Bunny hostname.
const CDN_BASE = import.meta.env?.VITE_TOUR_MEDIA_BASE_URL || 'https://adsmanager-staging.b-cdn.net/onboarding/refreshed-20260930-v1';
const MEDIA: Record<string, { src: string; poster?: string; label: string }> = {
  'tour-welcome.mp4': { src: `${CDN_BASE}/welcome.mp4`, poster: `${CDN_BASE}/welcome.jpg`, label: 'Welcome to AdMoji animation' },
  'tour-01-brand.mp4': { src: `${CDN_BASE}/brand.mp4`, poster: `${CDN_BASE}/brand.jpg`, label: 'Add and review your brand demonstration' },
  'tour-attach-brand.mp4': { src: `${CDN_BASE}/attach-brand.mp4`, poster: `${CDN_BASE}/attach-brand.jpg`, label: 'Choose your brand and attach references demonstration' },
  'tour-03-prompt.mp4': { src: `${CDN_BASE}/prompt.mp4`, poster: `${CDN_BASE}/prompt.jpg`, label: 'Write a creative brief demonstration' },
  'tour-04-dials.mp4': PAGE_GUIDE_MEDIA['dials-add-angles'],
  'tour-05-discover.mp4': { src: `${CDN_BASE}/discover.mp4`, poster: `${CDN_BASE}/discover.jpg`, label: 'Browse Inspiration demonstration' },
};

export type TourMedia = { src: string; poster?: string; label: string };

function LoopTourVideo({ media, wide }: { media: TourMedia; wide?: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => {
      setReducedMotion(preference.matches);
      if (preference.matches) video.current?.pause();
      else void video.current?.play().catch(() => { /* Leave the poster visible if autoplay is unavailable. */ });
    };
    sync();
    preference.addEventListener('change', sync);
    return () => preference.removeEventListener('change', sync);
  }, []);

  return (
    <div data-testid="tour-video-slot" style={{ position: 'relative', aspectRatio: wide ? '16 / 9' : '4 / 3', borderRadius: 8, overflow: 'hidden', background: '#F7F9FC', marginBottom: 12 }} onKeyDown={(event) => event.stopPropagation()}>
      {failed ? (
        <div style={{ position: 'relative', height: '100%' }}>
          {media.poster && <img src={media.poster} alt={media.label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
          <div role="status" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px 12px', background: 'rgba(255,255,255,.96)', textAlign: 'center', fontSize: 12, color: '#334155' }}>
            Video unavailable. You can still follow the tour below.
          </div>
        </div>
      ) : (
        <video ref={video} data-testid="tour-video" aria-label={media.label} src={media.src} poster={media.poster} muted loop playsInline disablePictureInPicture disableRemotePlayback tabIndex={-1} preload="metadata" onError={() => setFailed(true)} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
      )}
      {!failed && reducedMotion && media.poster && <img data-testid="tour-reduced-motion-poster" src={media.poster} alt={media.label} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#F7F9FC' }} />}
    </div>
  );
}

/** The page guide owns this finite player; global onboarding keeps LoopTourVideo. */
function OnceTourVideo({ media, wide }: { media: TourMedia; wide?: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const active = useRef(false);
  const [failed, setFailed] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [ended, setEnded] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  useEffect(() => {
    const element = video.current;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    active.current = true;
    const play = () => {
      if (!element) return;
      void element.play().then(() => {
        if (!active.current) element.pause();
      }).catch(() => {
        if (active.current && !preference.matches) setBlocked(true);
      });
    };
    const sync = () => {
      setReducedMotion(preference.matches);
      if (preference.matches) element?.pause();
      else if (element && !element.ended) play();
    };
    sync();
    preference.addEventListener('change', sync);
    return () => {
      active.current = false;
      preference.removeEventListener('change', sync);
      element?.pause();
    };
  }, [media.src]);

  const replay = () => {
    const element = video.current;
    if (!element) return;
    setEnded(false);
    setBlocked(false);
    element.pause();
    element.currentTime = 0;
    void element.play().then(() => {
      if (!active.current) element.pause();
    }).catch(() => {
      if (active.current) setBlocked(true);
    });
  };

  return <div data-testid="tour-video-slot" data-playback-state={failed ? 'failed' : reducedMotion ? 'reduced-motion' : blocked ? 'blocked' : ended ? 'ended' : 'ready'} style={{ borderRadius: 8, overflow: 'hidden', background: '#F7F9FC' }}>
    <div style={{ position: 'relative', aspectRatio: wide ? '16 / 9' : '4 / 3' }}>
    {failed ? <div style={{ position: 'relative', height: '100%' }}>
      {media.poster && <img src={media.poster} alt={media.label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
      <div role="status" className="page-guide-video-status">Video unavailable. You can still follow this lesson and continue through the guide.</div>
    </div> : <>
      <video ref={video} data-testid="tour-video" aria-label={media.label} src={media.src} poster={media.poster} muted playsInline disablePictureInPicture disableRemotePlayback tabIndex={-1} preload="metadata" onEnded={() => setEnded(true)} onPlaying={() => { setEnded(false); setBlocked(false); }} onError={() => setFailed(true)} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
      {reducedMotion && media.poster && <img data-testid="tour-reduced-motion-poster" src={media.poster} alt={media.label} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#F7F9FC' }} />}
      {reducedMotion && <span role="status" className="page-guide-video-status">Animation paused for reduced motion.</span>}
      {blocked && !reducedMotion && <span role="status" className="page-guide-video-status">Playback was blocked. Select Replay clip to try again, or continue through the guide.</span>}
    </>}
    </div>
    <div className="page-guide-video-controls">
      {!failed && !reducedMotion && <button className="page-guide-clip-replay" type="button" onClick={replay}>Replay clip</button>}
    </div>
  </div>;
}

export function TourVideo({ media, wide, playback = 'loop' }: { media: TourMedia; wide?: boolean; playback?: 'loop' | 'once' }) {
  return playback === 'once' ? <OnceTourVideo media={media} wide={wide} /> : <LoopTourVideo media={media} wide={wide} />;
}

export function TourVideoSlot({ file, wide }: { file?: string; wide?: boolean }) {
  if (!file) return null;
  const media = MEDIA[file];
  if (media) return <TourVideo key={file} media={media} wide={wide} />;
  return (
    <div data-testid="tour-video-slot" style={{ aspectRatio: wide ? '16 / 9' : '4 / 3', borderRadius: 8, border: `1.5px dashed ${PINK}`, background: 'rgba(123,31,64,0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: 8, marginBottom: 12 }}>
      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: PINK }}>Video</span>
      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, color: '#161616' }}>{file}</span>
    </div>
  );
}
